<script setup lang="ts">
import { pascalCase } from "scule";
import { resolveComponent } from "vue";
import { setResponseStatus } from "h3";
import { useNavigationSearch } from "#imports";

defineOptions({
  name: "PageResolver",
});

const { clearBreadcrumbs } = useBreadcrumbs();

const NOT_FOUND_COMPONENT = "errors/RoutingNotFound";
const { resolvePath } = useNavigationSearch();
const route = useRoute();
const { locale } = useI18n();
const routePath = route.path.replace(`${locale.value}`, "").replace("//", "/");

const { data: seoResult } = await useAsyncData(
  `cmsResponse${routePath}`,
  async () => {
    // For client links if the history state contains seo url information we can omit the api call
    if (import.meta.client) {
      if (history.state?.routeName) {
        return {
          routeName: history.state?.routeName,
          foreignKey: history.state?.foreignKey,
        };
      }
    }
    try {
      const seoUrl = await resolvePath(routePath);
      if (seoUrl) return seoUrl;
    } catch {}
    // Shopware stores category URLs with trailing slash and product URLs without.
    // If exact path fails, try the toggled form as a fallback.
    const altPath = routePath.endsWith('/') ? routePath.slice(0, -1) : routePath + '/';
    try {
      return await resolvePath(altPath);
    } catch {
      return null;
    }
  },
);

const resolvedRouteName = seoResult.value?.routeName;

// Use Shopware's seoPathInfo as the canonical URL (available from full API response)
if (seoResult.value?.seoPathInfo) {
  const canonical = '/' + seoResult.value.seoPathInfo;
  if (route.path !== canonical) {
    await navigateTo(canonical, { redirectCode: 301 });
  }
} else if (resolvedRouteName) {
  // Fallback for history.state case where seoPathInfo is not available
  if (resolvedRouteName === "frontend.navigation.page" && !route.path.endsWith("/")) {
    await navigateTo(route.path + "/", { redirectCode: 301 });
  } else if (resolvedRouteName === "frontend.detail.page" && route.path.endsWith("/")) {
    await navigateTo(route.path.slice(0, -1), { redirectCode: 301 });
  }
}

/** SEO URL missing or invalid — show not-found UI without throwing (avoids Nuxt error overlay / noisy console on client nav). */
const isSeoNotFound = !seoResult.value?.foreignKey;

if (import.meta.dev && isSeoNotFound) {
  console.warn(`[PageResolver] No SEO URL for path: ${routePath}`);
}

if (import.meta.server && isSeoNotFound) {
  const event = useRequestEvent();
  if (event) {
    setResponseStatus(event, 404);
  }
}

onBeforeRouteLeave(() => {
  clearBreadcrumbs();
});

const notFoundComponent = resolveComponent(pascalCase(NOT_FOUND_COMPONENT));
</script>

<template>
  <section class="layout-rendering-section">
    <PageResolverContent v-if="!isSeoNotFound && seoResult" :seo-result="seoResult" />
    <component
      v-else
      :is="notFoundComponent"
    />
  </section>
</template>
